import { FC, useCallback, useState } from 'react';
import { Box, Button, IconButton, Link, List, ListItem, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Img from '@barracuda/shared/components/Primitive/Img';
import { AccountPhoto } from '@barracuda/shared/components/AccountPhoto/AccountPhoto';
import { useAppContext } from '@/context/App.context';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { LocalizedNavLink } from '../LocalizedNavLink/LocalizedNavLink';
import { Logout } from '../Logout/Logout';
import { logoutMobileMenuButtonStyle } from '../Logout/Logout.styles';
import {
  logoLink,
  signInButtonNavbar,
  signUpButtonNavbar
} from '../HeaderNavbar/HeaderNavbar.style';
import { sideBarButtons } from '../SideBarNavigation/SideBarNavigation.styles';
import {
  MenuItemType,
  MenuSubItemType,
  renderMobileItems
} from '../NavbarMenuItem/navbarMenu.utils';
import Logo from '@/assets/images/pearson-logo-odyssey.svg';
import MenuExitIcon from '@/assets/images/menu__exit.svg';
import LogoutMobile from '@/assets/images/logout-mobile.svg';
import {
  Label,
  accountListItem,
  accountNavLinkStyle,
  fullWidthListItem,
  listItemNavBase,
  mobileMenuActionLabel,
  navLinkStyle,
  signedOffMobileMenuHeader
} from './MobileList.styles';

export interface MobileListItemInterface {
  link: string;
  label: string;
  replace?: boolean;
  small?: boolean;
}

interface MobileListProps {
  items: MobileListItemInterface[];
  setIsMenuOpen?: (value: boolean) => void;
}

interface CloseableProps {
  onClose: () => void;
}

interface MobileNavLinkItemProps extends CloseableProps {
  item: MobileListItemInterface;
}

const MobileNavLinkItem: FC<MobileNavLinkItemProps> = ({ item, onClose }) => (
  <ListItem disablePadding sx={listItemNavBase(item.small)}>
    <LocalizedNavLink
      className={({ isActive }) => (isActive && !item.replace ? 'active' : '')}
      style={navLinkStyle}
      end
      to={item.replace ? '' : item.link}
      onClick={() => (item.replace ? window.open(item.link) : onClose())}
    >
      <Typography sx={Label(item.small)}>{item.label}</Typography>
    </LocalizedNavLink>
  </ListItem>
);

interface MobileAccountItemProps extends CloseableProps {
  imageUri?: string;
  label: string;
}

const MobileAccountItem: FC<MobileAccountItemProps> = ({ imageUri, label, onClose }) => (
  <ListItem disablePadding sx={accountListItem}>
    <LocalizedNavLink to='/dashboard/account' style={accountNavLinkStyle} onClick={onClose}>
      <Stack direction='row' alignItems='center'>
        <AccountPhoto imageUri={imageUri} width='24px' height='24px' />
        <Typography sx={mobileMenuActionLabel}>{label}</Typography>
      </Stack>
    </LocalizedNavLink>
  </ListItem>
);

interface MobileLogoutItemProps extends CloseableProps {
  label: string;
}

const MobileLogoutItem: FC<MobileLogoutItemProps> = ({ label, onClose }) => (
  <ListItem disablePadding sx={fullWidthListItem}>
    <Logout sx={logoutMobileMenuButtonStyle} onLogout={onClose}>
      <Stack direction='row' alignItems='center'>
        <Img src={LogoutMobile} alt='' aria-hidden />
        <Typography sx={mobileMenuActionLabel}>{label}</Typography>
      </Stack>
    </Logout>
  </ListItem>
);

const SignedOffMobileMenu: FC<CloseableProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [activeItems, setActiveItems] = useState<MenuItemType[]>([]);

  const handleClose = useCallback(() => {
    setActiveItems([]);
    onClose();
  }, [onClose]);

  const handleMenuItemClick = useCallback(
    (item: MenuItemType | MenuSubItemType) => {
      if ('items' in item && item.items.length) {
        setActiveItems((current) =>
          current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item]
        );
        return;
      }

      if ('target' in item) {
        window.open(item.link, item.target);
      } else {
        navigate(item.link as string);
      }

      handleClose();
    },
    [handleClose, navigate]
  );

  const handleSignUp = useCallback(() => {
    navigate('/join');
    handleClose();
  }, [handleClose, navigate]);

  const handleSignIn = useCallback(() => {
    navigate('/login');
    handleClose();
  }, [handleClose, navigate]);

  return (
    <Stack>
      <Box sx={signedOffMobileMenuHeader}>
        <Link onClick={() => navigate('/')} sx={logoLink}>
          <Img src={Logo} alt={process.env.REACT_APP_GENERAL_PRODUCT_NAME} />
        </Link>
        <IconButton onClick={handleClose} aria-label={t('menuLabels.closeMenu')} type='button'>
          <Img src={MenuExitIcon} alt='' aria-hidden />
        </IconButton>
      </Box>

      <Box component='nav' aria-label={t('menuLabels.mainNavigation')}>
        <Stack sx={sideBarButtons}>
          <Button sx={signUpButtonNavbar} onClick={handleSignUp} id='header-sign-up'>
            {t('signUpText')}
          </Button>
          <Button size='large' sx={signInButtonNavbar} onClick={handleSignIn} id='header-sign-in'>
            {t('loginInText')}
          </Button>
        </Stack>
        {renderMobileItems(activeItems, handleMenuItemClick)}
      </Box>
    </Stack>
  );
};

const LoggedInMobileMenu: FC<MobileListProps> = ({ items, setIsMenuOpen }) => {
  const { user } = useAppContext();
  const { t } = useTranslation();

  const closeMenu = useCallback(() => {
    setIsMenuOpen?.(false);
  }, [setIsMenuOpen]);

  return (
    <Box component='nav' aria-label={t('menuLabels.mainNavigation')}>
      <List disablePadding sx={fullWidthListItem}>
        {items.map((item) => (
          <MobileNavLinkItem key={item.link} item={item} onClose={closeMenu} />
        ))}
        <MobileAccountItem
          imageUri={user?.imageUri}
          label={t('menuLabels.account')}
          onClose={closeMenu}
        />
        <MobileLogoutItem label={t('menuLabels.logOut')} onClose={closeMenu} />
      </List>
    </Box>
  );
};

export const MobileList: FC<MobileListProps> = ({ items, setIsMenuOpen }) => {
  const { isLoggedIn } = useAppContext();

  const closeMenu = useCallback(() => {
    setIsMenuOpen?.(false);
  }, [setIsMenuOpen]);

  if (!isLoggedIn) {
    return <SignedOffMobileMenu onClose={closeMenu} />;
  }

  return <LoggedInMobileMenu items={items} setIsMenuOpen={setIsMenuOpen} />;
};
